import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Modal } from "@playbookai/ui";
import {
  getProfileTest,
  startProfileTest,
  type ExecutionProfile,
} from "../../../api/admin-ai-profiles-api.js";

export function ProfileTestDialog({
  open,
  profile,
  queryKey,
  onSaved,
  onClose,
}: {
  /** Hoisted from the mount so the dialog can animate out (ADR-0091). */
  open: boolean;
  profile: ExecutionProfile;
  queryKey: readonly string[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [testId, setTestId] = useState<string | null>(
    profile.latest_test?.id ?? null,
  );
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* The dialog is retained while it closes (ADR-0091), so it now outlives a
     single opening: reseed from the profile and drop the previous attempt's
     failure, or reopening would show an error about a test nobody just ran. */
  useEffect(() => {
    if (!open) return;
    setTestId(profile.latest_test?.id ?? null);
    setError(null);
  }, [open]);
  const test = useQuery({
    queryKey: [...queryKey, "profile-test", testId],
    queryFn: () => getProfileTest(testId!),
    // Also gated on `open`: a closed dialog must not keep polling the provider.
    enabled: !!testId && open,
    refetchInterval: (query) =>
      query.state.status !== "error" &&
      ["pending", "running"].includes(query.state.data?.state ?? "pending")
        ? 1000
        : false,
  });
  const running =
    starting ||
    (!!testId &&
      (!test.data || ["pending", "running"].includes(test.data.state)));
  useEffect(() => {
    if (test.data && !["pending", "running"].includes(test.data.state))
      onSaved();
  }, [test.data?.state]);
  async function start() {
    setStarting(true);
    setError(null);
    try {
      setTestId((await startProfileTest(profile.id)).test_id);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not start profile test.",
      );
    } finally {
      setStarting(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Test ${profile.display_name}`}
      size="md"
    >
      <div className="grid gap-4">
        <p>
          Test the saved connection, model and settings together. This sends
          requests to the provider.
        </p>
        {(error || test.error) && (
          <Alert tone="critical">{error ?? test.error?.message}</Alert>
        )}
        <p role="status">
          {starting ? "Starting test…" : (test.data?.state ?? "Ready to test")}
        </p>
        {test.data?.message && <p>{test.data.message}</p>}
        {test.data?.error_code && <p>Error code: {test.data.error_code}</p>}
        {Object.entries(test.data?.checks ?? {})
          .filter(([, result]) => result === "failed")
          .map(([check]) => (
            <p key={check}>Failed check: {check.replaceAll("_", " ")}</p>
          ))}
        {test.data?.diagnostic_id && (
          <p>Test reference: {test.data.diagnostic_id}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            disabled={running || !profile.allowed_actions.test?.allowed}
            onClick={() => void start()}
          >
            {testId ? "Test again" : "Test profile"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
